import React, {useCallback, useEffect, useRef, useState} from 'react';
import {Col, Row} from 'react-bootstrap';
import {DateTime} from 'luxon';
import axios from 'axios';
import LoadingWrapper from '@/components/loading/LoadingWrapper';
import echarts from '@/utils/echartsCore';
import i18n from 'i18n';
import useOptimizedConfig from '@/hooks/useOptimizedConfig';
import {useDateChangeListener} from '@/utils/useDateDependentDataRefresh';
import {hasPermission} from '@/utils/permissions/permissionDebug';
import {outboundLogProviderLabel, outboundLogStatusLabel} from '@/views/admin/notification/outboundLogLabels';

const CHART_HEIGHT_PX = 280;

interface OutboundStatistics {
    totals: {all: number; sent: number; failed: number};
    successRatePercent: number | null;
    byStatus: {status: string; count: number}[];
    byProvider: {provider: string; count: number}[];
    byDay: {day: string; sent: number; failed: number; other: number; total: number}[];
    byRecipient?: {email: string; count: number}[];
}

function dayAxisLabel(isoDay: string): string {
    try {
        return DateTime.fromISO(isoDay, {zone: 'utc'}).toLocaleString({month: 'short', day: 'numeric'});
    } catch {
        return isoDay;
    }
}

const OutboundNotificationLogsDashboardView: React.FC = React.memo(() => {
    const {userData, loading: loadingUser} = useOptimizedConfig();
    const canView = hasPermission('fs.notification.outbound_log.read', userData?.permissions, true);
    const [stats, setStats] = useState<OutboundStatistics | null>(null);
    const [loading, setLoading] = useState<boolean>(false);

    const statusRef = useRef<HTMLDivElement>(null);
    const providerRef = useRef<HTMLDivElement>(null);
    const dailyRef = useRef<HTMLDivElement>(null);
    const recipientRef = useRef<HTMLDivElement>(null);

    const fetchStats = useCallback(async (): Promise<void> => {
        const url = (window as Window & {URLS?: {outboundNotificationLogStatistics?: string}}).URLS?.outboundNotificationLogStatistics;
        if (!canView || !url) {
            return;
        }
        setLoading(true);
        try {
            const {data} = await axios.get<OutboundStatistics>(url);
            setStats(data);
        } catch (e) {
            console.error(e);
            setStats(null);
        } finally {
            setLoading(false);
        }
    }, [canView]);

    useEffect(() => {
        fetchStats();
    }, [fetchStats]);

    useDateChangeListener(fetchStats);

    useEffect(() => {
        if (!stats || loadingUser) {
            return;
        }
        const statusEl = statusRef.current;
        const providerEl = providerRef.current;
        const dailyEl = dailyRef.current;
        const recipientEl = recipientRef.current;
        if (!statusEl || !providerEl || !dailyEl || !recipientEl) {
            return;
        }

        const statusChart = echarts.init(statusEl);
        const providerChart = echarts.init(providerEl);
        const dailyChart = echarts.init(dailyEl);
        const recipientChart = echarts.init(recipientEl);

        const handleResize = (): void => {
            [statusChart, providerChart, dailyChart, recipientChart].forEach((c) => {
                if (!c.isDisposed()) {
                    c.resize();
                }
            });
        };
        let resizeObserver: ResizeObserver | null = null;
        if (typeof ResizeObserver !== 'undefined') {
            resizeObserver = new ResizeObserver(handleResize);
            resizeObserver.observe(statusEl);
            resizeObserver.observe(providerEl);
            resizeObserver.observe(dailyEl);
            resizeObserver.observe(recipientEl);
        }
        window.addEventListener('resize', handleResize);

        const textStyle = {fontFamily: '"Rubik", sans-serif', fontSize: 12};

        statusChart.setOption(
            {
                textStyle,
                tooltip: {trigger: 'item'},
                series: [
                    {
                        type: 'pie',
                        radius: ['32%', '58%'],
                        data: stats.byStatus.map((s) => ({
                            name: outboundLogStatusLabel(s.status),
                            value: s.count,
                        })),
                    },
                ],
            },
            true,
        );

        providerChart.setOption(
            {
                textStyle,
                tooltip: {trigger: 'item'},
                series: [
                    {
                        type: 'pie',
                        radius: ['32%', '58%'],
                        data: stats.byProvider.map((p) => ({
                            name: outboundLogProviderLabel(p.provider),
                            value: p.count,
                        })),
                    },
                ],
            },
            true,
        );

        const dayLabels = stats.byDay.map((d) => dayAxisLabel(d.day));
        const sentSeries = stats.byDay.map((d) => d.sent);
        const failedSeries = stats.byDay.map((d) => d.failed);
        const hasOther = stats.byDay.some((d) => d.other > 0);
        const dayCount = dayLabels.length;
        const labelInterval = Math.max(0, Math.ceil(dayCount / 12) - 1);
        const rotate = dayCount > 12 ? 28 : 0;

        const dailySeries: Array<{name: string; type: 'line'; data: number[]; smooth?: boolean}> = [
            {name: i18n.t('outbound_log_status_sent.text'), type: 'line', data: sentSeries, smooth: true},
            {name: i18n.t('outbound_log_status_failed.text'), type: 'line', data: failedSeries, smooth: true},
        ];
        if (hasOther) {
            dailySeries.push({
                name: i18n.t('outbound_log_chart_other_status.text'),
                type: 'line',
                data: stats.byDay.map((d) => d.other),
                smooth: true,
            });
        }

        dailyChart.setOption(
            {
                textStyle,
                tooltip: {trigger: 'axis'},
                legend: {top: 0},
                grid: {left: 48, right: 16, top: 36, bottom: rotate ? 48 : 28},
                xAxis: {
                    type: 'category',
                    data: dayLabels,
                    axisLabel: {rotate, interval: labelInterval, hideOverlap: true},
                },
                yAxis: {type: 'value', minInterval: 1},
                series: dailySeries,
            },
            true,
        );

        const byRecipient = stats.byRecipient ?? [];
        const topRecipients = [...byRecipient].slice(0, 20).reverse();
        const recipientLabels = topRecipients.map((r) => (r.email.length > 42 ? `${r.email.slice(0, 40)}…` : r.email));
        const recipientCounts = topRecipients.map((r) => r.count);
        const recipientLeft = Math.min(280, 44 + Math.min(220, ...recipientLabels.map((l) => l.length * 7)));
        recipientChart.setOption(
            {
                textStyle,
                tooltip: {trigger: 'axis', axisPointer: {type: 'shadow'}},
                grid: {left: recipientLeft, right: 16, top: 8, bottom: 24},
                xAxis: {type: 'value', minInterval: 1},
                yAxis: {type: 'category', data: recipientLabels, axisLabel: {interval: 0, fontSize: 11}},
                series: [
                    {
                        type: 'bar',
                        name: i18n.t('outbound_log_chart_recipients_series.text'),
                        data: recipientCounts,
                    },
                ],
            },
            true,
        );

        return (): void => {
            window.removeEventListener('resize', handleResize);
            if (resizeObserver) {
                resizeObserver.disconnect();
            }
            statusChart.dispose();
            providerChart.dispose();
            dailyChart.dispose();
            recipientChart.dispose();
        };
    }, [stats, loadingUser]);

    if (loadingUser) {
        return (
            <LoadingWrapper loading={true} loadingText={i18n.t('jt_loading_data_general.text')} minHeight="400px">
                <div />
            </LoadingWrapper>
        );
    }

    if (!canView) {
        return (
            <div className="alert alert-danger" role="alert">
                {i18n.t('outbound_log_no_permission.text')}
            </div>
        );
    }

    return (
        <>
            {loading ? (
                <LoadingWrapper loading={true} loadingText={i18n.t('jt_loading_data_general.text')} minHeight="400px">
                    <div />
                </LoadingWrapper>
            ) : stats && stats.totals.all > 0 ? (
                <>
                    <Row className="g-3 mb-3">
                        <Col xs={6} md={3}>
                            <div className="card border bg-white h-100 shadow-sm">
                                <div className="card-body py-3 px-3 small">
                                    <div className="text-muted">{i18n.t('outbound_log_stats_total.text')}</div>
                                    <div className="h5 mb-0">{stats.totals.all}</div>
                                </div>
                            </div>
                        </Col>
                        <Col xs={6} md={3}>
                            <div className="card border bg-white h-100 shadow-sm">
                                <div className="card-body py-3 px-3 small">
                                    <div className="text-muted">{i18n.t('outbound_log_status_sent.text')}</div>
                                    <div className="h5 mb-0">{stats.totals.sent}</div>
                                </div>
                            </div>
                        </Col>
                        <Col xs={6} md={3}>
                            <div className="card border bg-white h-100 shadow-sm">
                                <div className="card-body py-3 px-3 small">
                                    <div className="text-muted">{i18n.t('outbound_log_status_failed.text')}</div>
                                    <div className="h5 mb-0">{stats.totals.failed}</div>
                                </div>
                            </div>
                        </Col>
                        <Col xs={6} md={3}>
                            <div className="card border bg-white h-100 shadow-sm">
                                <div className="card-body py-3 px-3 small">
                                    <div className="text-muted">{i18n.t('outbound_log_stats_success_rate.text')}</div>
                                    <div className="h5 mb-0">
                                        {stats.successRatePercent != null ? `${stats.successRatePercent}%` : '—'}
                                    </div>
                                </div>
                            </div>
                        </Col>
                    </Row>
                    <Row className="g-3 mb-3">
                        <Col xs={12} md={4}>
                            <div className="card border bg-white h-100 shadow-sm">
                                <div className="card-body pt-3 pb-2 px-3">
                                    <div className="small text-muted mb-2">{i18n.t('outbound_log_chart_status_title.text')}</div>
                                    <div ref={statusRef} style={{height: CHART_HEIGHT_PX}} className="w-100 bg-white" />
                                </div>
                            </div>
                        </Col>
                        <Col xs={12} md={4}>
                            <div className="card border bg-white h-100 shadow-sm">
                                <div className="card-body pt-3 pb-2 px-3">
                                    <div className="small text-muted mb-2">{i18n.t('outbound_log_chart_provider_title.text')}</div>
                                    <div ref={providerRef} style={{height: CHART_HEIGHT_PX}} className="w-100 bg-white" />
                                </div>
                            </div>
                        </Col>
                        <Col xs={12} md={4}>
                            <div className="card border bg-white h-100 shadow-sm">
                                <div className="card-body pt-3 pb-2 px-3">
                                    <div className="small text-muted mb-2">{i18n.t('outbound_log_chart_daily_title.text')}</div>
                                    <div ref={dailyRef} style={{height: CHART_HEIGHT_PX}} className="w-100 bg-white" />
                                </div>
                            </div>
                        </Col>
                    </Row>
                    <Row className="g-3 mb-3">
                        <Col md={12}>
                            <div className="card border bg-white h-100 shadow-sm">
                                <div className="card-body pt-3 pb-2 px-3">
                                    <div className="small text-muted mb-2">{i18n.t('outbound_log_chart_recipients_title.text')}</div>
                                    <div
                                        ref={recipientRef}
                                        style={{height: Math.max(CHART_HEIGHT_PX, 24 * Math.min(20, Math.max(3, (stats.byRecipient ?? []).length)))}}
                                        className="w-100 bg-white"
                                    />
                                </div>
                            </div>
                        </Col>
                    </Row>
                </>
            ) : (
                <p className="text-muted">{i18n.t('outbound_log_stats_empty.text')}</p>
            )}
        </>
    );
});

OutboundNotificationLogsDashboardView.displayName = 'OutboundNotificationLogsDashboardView';

export default OutboundNotificationLogsDashboardView;
